const isServer = typeof window === 'undefined'
const isNextEdgeRuntime = process.env.NEXT_RUNTIME === 'edge'
const isDevelopment = process.env.NODE_ENV === 'development'

type LogFn = {
  (obj: Object, msg?: string): void
  (msg: string, obj?: Object): void
}

interface Logger {
  debug: LogFn
  info: LogFn
  warn: LogFn
  error: LogFn
  fatal: LogFn
}

// Helper to normalize arguments order
const normalizeArgs = (first: string | Object, second?: string | Object) => {
  if (typeof first === 'string') {
    return { msg: first, obj: second || {} }
  }
  return { msg: second as string, obj: first }
}

// Error/DOMException instances store message/stack/name as non-enumerable
// own properties, so JSON.stringify silently collapses them to `{}` -
// serialize any Error-shaped value before it reaches JSON.stringify below,
// so simpleLogger.error/warn/fatal never logs an unusably empty error.
const serializeLoggableValue = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      ...('cause' in value ? { cause: serializeLoggableValue((value as { cause?: unknown }).cause) } : {})
    }
  }
  return value
}

const serializeLoggableObject = (obj: Object) => {
  if (!obj || typeof obj !== 'object') return obj

  const entries = Object.entries(obj).map(([key, value]) => [key, serializeLoggableValue(value)])
  return Object.fromEntries(entries)
}

// Simple logger for client-side and edge runtime
const simpleLogger: Logger = {
  debug: (first: string | Object, second?: string | Object) => {
    if (!isDevelopment) return
    const { msg, obj } = normalizeArgs(first, second)
    // TODO console.log(JSON.stringify({ ...obj, msg, level: "debug" }))
  },
  info: (first: string | Object, second?: string | Object) => {
    const { msg, obj } = normalizeArgs(first, second)
    // TODO console.log(JSON.stringify({ ...obj, msg, level: "info" }))
  },
  warn: (first: string | Object, second?: string | Object) => {
    const { msg, obj } = normalizeArgs(first, second)
    console.warn(JSON.stringify({ ...serializeLoggableObject(obj), msg, level: 'warn' }))
  },
  error: (first: string | Object, second?: string | Object) => {
    const { msg, obj } = normalizeArgs(first, second)
    console.error(JSON.stringify({ ...serializeLoggableObject(obj), msg, level: 'error' }))
  },
  fatal: (first: string | Object, second?: string | Object) => {
    const { msg, obj } = normalizeArgs(first, second)
    console.error(JSON.stringify({ ...serializeLoggableObject(obj), msg, level: 'fatal' }))
  }
}

// Only use pino in server environment (not edge runtime)
let logger: Logger = simpleLogger

if (isServer && !isNextEdgeRuntime) {
  try {
    // Reuse a single pino instance per process to avoid MaxListenersExceededWarning
    // (each pino + pino-pretty instance adds listeners to process.stdout/stderr)
    const globalKey = '__losi_pino_logger'
    const globalForLogger = globalThis as unknown as { [key: string]: unknown }
    type PinoInstance = { debug: LogFn; info: LogFn; warn: LogFn; error: LogFn; fatal: LogFn }
    let pinoLogger = globalForLogger[globalKey] as PinoInstance | undefined

    if (!pinoLogger) {
      // Allow more listeners on stdout/stderr when using pino-pretty (worker threads / HMR re-load logger)
      if (typeof process.stdout.setMaxListeners === 'function') {
        process.stdout.setMaxListeners(20)
      }
      if (typeof process.stderr.setMaxListeners === 'function') {
        process.stderr.setMaxListeners(20)
      }
      const pino = require('pino')
      pinoLogger = pino({
        level: isDevelopment ? 'debug' : 'info',
        transport: isDevelopment
          ? {
              target: 'pino-pretty',
              options: {
                colorize: true
              }
            }
          : undefined
      })
      globalForLogger[globalKey] = pinoLogger
    }

    const resolved = pinoLogger as PinoInstance
    // Wrap pino logger to handle both argument orders
    logger = {
      debug: (first: string | Object, second?: string | Object) => {
        const { msg, obj } = normalizeArgs(first, second)
        resolved.debug(obj, msg)
      },
      info: (first: string | Object, second?: string | Object) => {
        const { msg, obj } = normalizeArgs(first, second)
        resolved.info(obj, msg)
      },
      warn: (first: string | Object, second?: string | Object) => {
        const { msg, obj } = normalizeArgs(first, second)
        resolved.warn(obj, msg)
      },
      error: (first: string | Object, second?: string | Object) => {
        const { msg, obj } = normalizeArgs(first, second)
        resolved.error(obj, msg)
      },
      fatal: (first: string | Object, second?: string | Object) => {
        const { msg, obj } = normalizeArgs(first, second)
        resolved.fatal(obj, msg)
      }
    }
  } catch (e) {
    console.warn('Failed to initialize pino logger, falling back to simple logger')
  }
}

export { logger }
